import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { LetDirective } from '@ngrx/component';
import { Observable, Subject, map, merge, startWith, tap } from 'rxjs';

import { TicketPriority } from '@mp/shared/zendesk/domain';

import { MailSenderDetails, ZendeskSupportFormDialogData } from '../../models';
import { SupportAdditionalInfoComponent } from '../support-additional-info/support-additional-info.component';
import { SupportMessageAttachmentsComponent } from '../support-message-attachments/support-message-attachments.component';
import { SupportMessagePrioritySelectorComponent } from '../support-message-priority-selector/support-message-priority-selector.component';

@Component({
  selector: 'mp-zendesk-support-form-dialog',
  templateUrl: './zendesk-support-form-dialog.component.html',
  styleUrl: './zendesk-support-form-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    LetDirective,

    MatButton,
    MatIconButton,
    MatDialogTitle,
    MatDialogClose,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    CdkTextareaAutosize,
    MatTooltip,
    MatIcon,

    SupportMessagePrioritySelectorComponent,
    SupportMessageAttachmentsComponent,
    SupportAdditionalInfoComponent,
  ],
})
export class ZendeskSupportFormDialogComponent {
  private readonly data: ZendeskSupportFormDialogData = inject<ZendeskSupportFormDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef: MatDialogRef<ZendeskSupportFormDialogComponent> = inject(
    MatDialogRef<ZendeskSupportFormDialogComponent>,
  );
  private readonly fb: NonNullableFormBuilder = inject(NonNullableFormBuilder);

  readonly priorityControl: FormControl<TicketPriority> = this.fb.control(TicketPriority.Normal, Validators.required);
  readonly subjectControl: FormControl<string> = this.fb.control('', Validators.required);
  readonly bodyControl: FormControl<string> = this.fb.control('', Validators.required);
  readonly attachmentsControl: FormControl<File[]> = this.fb.control([]);

  readonly zendeskSupportForm = this.fb.group({
    priority: this.priorityControl,
    subject: this.subjectControl,
    body: this.bodyControl,
    attachments: this.attachmentsControl,
  });

  protected readonly mailSenderDetails: MailSenderDetails = this.data.mailSenderDetails;

  private readonly MAX_FILE_SIZE_IN_MB: number = 50;

  private readonly MB_MULTIPLIER: number = 1024 * 1024;

  readonly MAX_ATTACHMENTS_NUMBER: number = 5;

  readonly MAX_FILE_SIZE: number = this.MAX_FILE_SIZE_IN_MB * this.MB_MULTIPLIER;

  readonly attachmentsInfoTooltipText: string = `
  Maximale Anzahl an erlaubten Anhängen: ${this.MAX_ATTACHMENTS_NUMBER}.
  Die jeweilige Datei darf nicht größer sein als ${this.MAX_FILE_SIZE_IN_MB} MB.
  `;

  private readonly _ticketSend$: Subject<void> = new Subject<void>();

  readonly ticketSendInProgress$: Observable<boolean> = merge(
    this._ticketSend$.asObservable().pipe(map(() => true)),
    this.data.ticketCreationError$.pipe(map(() => false)),
  ).pipe(startWith(false));

  constructor() {
    this.data.ticketCreationSuccess$
      .pipe(
        tap(() => this.dialogRef.close()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  onZendeskSupportMessageSend(): void {
    this._ticketSend$.next();
    this.data.sendTicket(this.zendeskSupportForm.getRawValue());
  }
}
